import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { message } from '@myua/kit';
import { getAssetLabLogger } from '../../app/logs';
import {
  AiApiKey,
  AiProvider,
  GEMINI_KEYS_URL,
  OPENAI_KEYS_URL,
  CreateAiApiKeyPayload,
  usd,
} from '../aiApi';
import { TM } from '../tokens';
import { ArrowDownIcon, CloseIcon, ExternalLinkIcon, KeyIcon } from '../icons';
import { MagicBtn } from './ui';

// The prompt-toolbar control for the *backend* path (MYUA-16447): manages the
// user's AI provider keys held server-side, replacing the direct-Gemini
// ApiKeyControl (which stays for the disabled key-in-browser path).
//
// Shape and behaviour deliberately mirror ApiKeyControl — same pill + popover,
// same compact / placement / controlled-open props — so the toolbar stays
// visually identical; only the contents differ:
//   • the list of keys already registered (with scope + remaining budget), and
//   • a form to register a new one.
//
// The raw secret leaves the browser exactly once, in POST /ai/api-keys/; it is
// stored encrypted and every generation afterwards references it by id.

// Backend bound (mirrored here so the user gets an instant message instead of a
// round-trip 422).
const MIN_KEY_LENGTH = 8;

const PROVIDER_LABEL: Record<AiProvider, string> = {
  gemini: 'Gemini (Google AI Studio)',
  openai: 'Image GPT (OpenAI)',
};

const PROVIDER_KEYS_URL: Record<AiProvider, string> = {
  gemini: GEMINI_KEYS_URL,
  openai: OPENAI_KEYS_URL,
};

// Badge / caption for a key's provider. Written as "gemini or else OpenAI"
// rather than a lookup table so a key stored under an older spelling
// ('image_gpt') still renders a name instead of `undefined`.
const providerShort = (provider: string): string => (provider === 'gemini' ? 'Gemini' : 'OpenAI');

// Only 'user' keys belong to the person looking at them; corporate / team keys
// are provisioned by an admin and DELETE would answer 403/404, so the remove
// action is not offered for those.
const SCOPE_LABEL: Record<string, string> = {
  user: 'personal',
  team: 'team',
  corporate: 'corporate',
};

const keyTitle = (key: AiApiKey): string => key.name?.trim() || providerShort(key.provider);

// The name is mandatory: the column is nullable but the backend answers 500 for a
// key stored without one, so the form never lets an empty value through and
// suggests a ready-made name instead of demanding the user invent one.
//
// (name, owner) is unique — a second key for the same provider would collide with
// the plain "Gemini key", so the suggestion is numbered past whatever is already
// in the list.
const suggestKeyName = (provider: AiProvider, keys: AiApiKey[]): string => {
  const base = `${providerShort(provider)} key`;
  const taken = new Set(keys.map((k) => k.name?.trim().toLowerCase()).filter(Boolean));
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
};

const badgeStyle: CSSProperties = {
  fontSize: 9.5,
  fontWeight: 700,
  padding: '1px 5px',
  borderRadius: 3,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
};

// One registered key: what it is, how much budget is left for this user, and
// (for personal keys) a way to drop it.
const KeyRow: React.FC<{ apiKey: AiApiKey; onRemove: (id: number) => void; busy: boolean }> = ({
  apiKey,
  onRemove,
  busy,
}) => {
  const own = apiKey.access_scope === 'user';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 6,
        border: `1px solid ${TM.border}`,
        background: TM.blueWash,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: TM.text }}>{keyTitle(apiKey)}</span>
          <span style={{ ...badgeStyle, background: TM.blueFaint, color: TM.blue }}>
            {providerShort(apiKey.provider)}
          </span>
          <span style={{ ...badgeStyle, background: '#fff', color: TM.textFaint }}>
            {SCOPE_LABEL[apiKey.access_scope] ?? apiKey.access_scope}
          </span>
        </div>
        <div style={{ fontSize: 10.5, color: TM.textMute, marginTop: 3 }}>
          <b style={{ color: apiKey.user_remaining_usd > 0 ? TM.green : TM.orange }}>
            {usd(apiKey.user_remaining_usd)}
          </b>{' '}
          left for you this month · key cap {usd(apiKey.monthly_limit_usd)}
        </div>
        <div style={{ fontSize: 10, color: TM.textFaint, marginTop: 2, wordBreak: 'break-word' }}>
          {apiKey.models.length > 0 ? apiKey.models.join(', ') : 'No models available on this key'}
        </div>
      </div>
      {own && (
        <button
          type="button"
          title="Remove this key"
          aria-label={`Remove ${keyTitle(apiKey)}`}
          disabled={busy}
          onClick={() => onRemove(apiKey.id)}
          style={{
            width: 22,
            height: 22,
            padding: 0,
            flex: 'none',
            borderRadius: 4,
            border: `1px solid ${TM.border}`,
            background: '#fff',
            color: TM.textFaint,
            cursor: busy ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloseIcon size={10} />
        </button>
      )}
    </div>
  );
};

export const AiKeysControl: React.FC<{
  keys: AiApiKey[];
  loading: boolean;
  // Set when the *list* could not be loaded (403 / 5xx) — the form is replaced
  // by the message plus a retry, since we don't know what the user already has.
  error: string | null;
  onReload: () => void;
  onAddKey: (payload: CreateAiApiKeyPayload) => Promise<AiApiKey | null>;
  onRemoveKey: (id: number) => Promise<boolean>;
  // Icon-only square button (no label/arrow). Used in the compact prompt toolbar.
  compact?: boolean;
  // Where the popover opens relative to the button. 'bottom-right' (default) for
  // the header; 'top-left' when the control sits in the bottom prompt toolbar.
  placement?: 'bottom-right' | 'top-left';
  // Optional controlled open state, so the canvas's "Connect API Key" button can
  // open this very popover.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}> = ({
  keys,
  loading,
  error,
  onReload,
  onAddKey,
  onRemoveKey,
  compact = false,
  placement = 'bottom-right',
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const [provider, setProvider] = useState<AiProvider>('gemini');
  const [secret, setSecret] = useState('');
  const [name, setName] = useState('');
  // Once the user types their own name the provider-derived suggestion stops
  // overwriting it.
  const [nameTouched, setNameTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  // Collapsed while the user already has keys — the list is the common case and
  // the form would push it out of view.
  const [formOpen, setFormOpen] = useState(false);

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const assetLabLogger = useSelector(getAssetLabLogger);
  const connected = keys.length > 0;
  const dropPos: CSSProperties =
    placement === 'top-left' ? { bottom: 36, left: 0 } : { top: 36, right: 0 };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, setOpen]);

  // With no keys yet the form is the whole point of opening the popover.
  useEffect(() => {
    if (!open) return;
    setFormOpen(keys.length === 0);
  }, [open, keys.length]);

  // Keep the suggested name in step with the chosen provider (and with the keys
  // already registered) until the user writes their own.
  useEffect(() => {
    if (!formOpen || nameTouched) return;
    setName(suggestKeyName(provider, keys));
  }, [formOpen, nameTouched, provider, keys]);

  // Focus the secret field whenever the form becomes visible, and again when the
  // user returns from the provider's key page, so the copied key can be pasted
  // straight away.
  useEffect(() => {
    if (!open || !formOpen) return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    const onFocus = () => inputRef.current?.focus();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [open, formOpen]);

  const openProviderKeys = () => {
    window.open(
      PROVIDER_KEYS_URL[provider],
      'ai_provider_apikey',
      'noopener,noreferrer,width=960,height=760',
    );
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const save = async () => {
    const apiKey = secret.trim();
    if (apiKey.length < MIN_KEY_LENGTH) {
      void message.warning(`Paste the full key — at least ${MIN_KEY_LENGTH} characters.`);
      return;
    }
    const keyName = name.trim();
    if (!keyName) {
      void message.warning('Give the key a name so you can tell it apart later.');
      return;
    }
    setSaving(true);
    // access_scope is intentionally omitted: the backend defaults to 'user', so
    // a key registered here is visible only to its owner. Corporate / team keys
    // are provisioned by an admin. The monthly limit is likewise not sent — the
    // backend assigns the default, and admins adjust it per key in Django Admin.
    const created = await onAddKey({
      provider,
      api_key: apiKey,
      name: keyName,
    });
    setSaving(false);
    if (!created) return;
    assetLabLogger({
      action: 'Magic Studio',
      sub_action: 'Connect API Key',
      extra: { text_1: provider, bool_1: connected },
    });
    setSecret('');
    setName('');
    setNameTouched(false);
    setFormOpen(false);
  };

  const remove = async (id: number) => {
    setSaving(true);
    await onRemoveKey(id);
    setSaving(false);
  };

  const triggerTitle = connected
    ? `${keys.length} AI key${keys.length === 1 ? '' : 's'} available`
    : 'Add your Gemini or OpenAI API key';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          title={triggerTitle}
          style={{
            width: 32,
            height: 32,
            padding: 0,
            borderRadius: 6,
            border: `1px solid ${connected ? TM.greenSoft : TM.border}`,
            background: connected ? '#F2FBF4' : '#fff',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: connected ? TM.green : TM.textFaint,
          }}
        >
          <KeyIcon size={15} />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          title={triggerTitle}
          style={{
            height: 32,
            padding: '0 10px',
            borderRadius: 6,
            border: `1px solid ${connected ? TM.greenSoft : TM.border}`,
            background: connected ? '#F2FBF4' : '#fff',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 12,
            color: TM.text,
          }}
        >
          <span style={{ display: 'inline-flex', color: connected ? TM.green : TM.textFaint }}>
            <KeyIcon size={13} />
          </span>
          <span style={{ fontWeight: 600 }}>{connected ? 'API keys' : 'Add API key'}</span>
          <ArrowDownIcon size={10} />
        </button>
      )}

      {open && (
        <div
          style={{
            position: 'absolute',
            ...dropPos,
            width: 380,
            maxHeight: 460,
            overflowY: 'auto',
            padding: 14,
            background: '#fff',
            border: `1px solid ${TM.border}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: TM.text }}>AI provider keys</div>
            <div style={{ fontSize: 11, color: TM.textFaint, lineHeight: 1.5, marginTop: 2 }}>
              Your key is encrypted and stored on our server; requests to Gemini / OpenAI are made
              from our backend, never from this browser.
            </div>
          </div>

          {loading && <div style={{ fontSize: 12, color: TM.textMute }}>Loading your keys…</div>}

          {!loading && error && (
            <div
              style={{
                fontSize: 11.5,
                color: TM.textMute,
                lineHeight: 1.5,
                padding: '8px 10px',
                borderRadius: 6,
                background: TM.bgSoft,
                border: `1px solid ${TM.border}`,
              }}
            >
              <div style={{ color: TM.orange, fontWeight: 700, marginBottom: 4 }}>
                Could not load your keys
              </div>
              <div style={{ marginBottom: 8 }}>{error}</div>
              <MagicBtn kind="secondary" size="sm" onClick={onReload}>
                Try again
              </MagicBtn>
            </div>
          )}

          {!loading && !error && (
            <>
              {keys.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {keys.map((k) => (
                    <KeyRow key={k.id} apiKey={k} onRemove={remove} busy={saving} />
                  ))}
                </div>
              )}

              {!formOpen ? (
                <MagicBtn
                  kind="secondary"
                  onClick={() => setFormOpen(true)}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  Add another key
                </MagicBtn>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {keys.length > 0 && (
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: TM.textFaint,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                      }}
                    >
                      Add a key
                    </div>
                  )}

                  <select
                    className="al-magic-input"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value as AiProvider)}
                    aria-label="Provider"
                  >
                    <option value="gemini">{PROVIDER_LABEL.gemini}</option>
                    <option value="openai">{PROVIDER_LABEL.openai}</option>
                  </select>

                  <MagicBtn
                    kind="magic"
                    icon={<ExternalLinkIcon size={13} />}
                    onClick={openProviderKeys}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    Get a key from {providerShort(provider)}
                  </MagicBtn>

                  <input
                    ref={inputRef}
                    className="al-magic-input"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="Paste your API key"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void save();
                    }}
                  />

                  <input
                    className="al-magic-input"
                    type="text"
                    required
                    autoComplete="off"
                    spellCheck={false}
                    aria-label="Key name"
                    placeholder="Key name — e.g. “My Gemini key”"
                    value={name}
                    onChange={(e) => {
                      setNameTouched(true);
                      setName(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void save();
                    }}
                    style={
                      name.trim() ? undefined : { borderColor: TM.orange, background: '#FFFBF5' }
                    }
                  />

                  <div style={{ display: 'flex', gap: 8 }}>
                    <MagicBtn
                      kind="primary"
                      disabled={saving}
                      onClick={() => void save()}
                      style={{ flex: 1, justifyContent: 'center' }}
                    >
                      {saving ? 'Saving…' : 'Save key'}
                    </MagicBtn>
                    {keys.length > 0 && (
                      <MagicBtn kind="ghost" onClick={() => setFormOpen(false)}>
                        Cancel
                      </MagicBtn>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div
            style={{
              fontSize: 10.5,
              color: TM.textMute,
              lineHeight: 1.55,
              padding: '8px 10px',
              borderRadius: 6,
              background: TM.bgSoft,
              border: `1px solid ${TM.border}`,
            }}
          >
            <b style={{ color: TM.text }}>Spending is capped.</b> Every key gets a default monthly
            limit; once it is used up, generations are refused until the next month. Contact your
            administrator to change or remove a limit. Keys you add are visible only to you.
          </div>
        </div>
      )}
    </div>
  );
};
